import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import AddMetricButton from './AddMetricButton';
import usePopup from '../../../../hooks/usePopup';
import useMetric from '../../../../hooks/useMetric';
import MetricOptionsContext from '../../../../contexts/MetricOptionsContext';
import isDefined from '../../../../isDefined';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import FilterPlatesProvider from '../../../../contextProviders/FilterPlatesProvider';
import metricTypeCheckers from '../../../../types/metricTypeCheckers';
import EntityDetailsContext from '../../../../screens/EntityDetailsShow/EntityDetailsContext';
import STORE from '../../../../store';
import getIdentifier from '../../../../getIdentifier';

const AddMetricButtonContainer = ({
  onAddMetricItem,
}: {
  onAddMetricItem: (newMetricItem: MetricListItemType) => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);

  const { isOpen, open, close } = usePopup();
  const [reportDrillDownId, setReportDrillDownId] = useState<
    string | undefined
  >(undefined);
  const [boardDrillDownId, setBoardDrillDownId] = useState<string | undefined>(
    undefined,
  );
  const [name, setName] = useState<string>('');
  const [metricId, setMetricId] = useState<string | undefined>('');
  const metric = useMetric(metricId);
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>([]);
  const [scope, setScope] = useState<FilterPlateType[]>([]);
  const [isDefaultFiltersDisabled, setIsDefaultFiltersDisabled] =
    useState<boolean>(false);
  const { normalMetrics } = useContext(MetricOptionsContext);
  const entityDetailsContext = useContext(EntityDetailsContext);

  const createNewMetricItem = async () => {
    if (metricId && name !== '') {
      const savedFilterId = getIdentifier();
      await STORE.filterDefinitions
        .getLegacySavedFiltersRef({ accountId: selectedAccountId })
        .doc(savedFilterId)
        .set({
          version: '2',
          id: savedFilterId,
          scope,
          drillDowns,
          source: 'metric-list-item',
          dateField: 'date',
        });

      const newItem: MetricListItemType = {
        id: getIdentifier(),
        name,
        metricId,
        savedFilterId,
        reportDrillDownId,
        boardDrillDownId,
        isEntityFilterEnabled: !!entityDetailsContext,
        isDefaultFiltersDisabled,
      };
      onAddMetricItem(newItem);
      close();
      setMetricId(undefined);
      setName('');
      setDrillDowns([]);
      setScope([]);
      setBoardDrillDownId(undefined);
      setReportDrillDownId(undefined);
    }
  };

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  useEffect(() => {
    if (!metricId) {
      return;
    }

    if (!metric) {
      return;
    }

    setName(metric.name);
  }, [metric, metricId]);

  const dataSources = (() => {
    if (!metricId) {
      return [];
    }
    if (!metric) {
      return [];
    }
    const sources = [] as string[];
    if (metricTypeCheckers.isCompoundMetric(metric)) {
      const baseMetrics = metric.metricIds
        .map((mId) => {
          return normalMetrics.find((m) => m.id === mId);
        })
        .filter(isDefined);

      baseMetrics.forEach((m) => {
        sources.push(m.dataType);
      });
    } else if (metricTypeCheckers.isSpecialMetric(metric)) {
      sources.push(metric.dataType);
    } else {
      sources.push(metric.dataType);
    }

    return sources;
  })();

  return (
    <FilterPlatesProvider
      drillDowns={drillDowns}
      setDrillDowns={setDrillDowns}
      scope={scope}
      setScope={setScope}
      dataTypes={dataSources}
      isDefaultFiltersDisabled={isDefaultFiltersDisabled}
      setIsDefaultFiltersDisabled={setIsDefaultFiltersDisabled}
    >
      <AddMetricButton
        isOpen={isOpen}
        open={open}
        close={close}
        name={name}
        onNameChanged={onNameChanged}
        createNewMetricItem={createNewMetricItem}
        metricId={metricId}
        setMetricId={setMetricId}
        reportDrillDownId={reportDrillDownId}
        setReportDrillDownId={setReportDrillDownId}
        boardDrillDownId={boardDrillDownId}
        setBoardDrillDownId={setBoardDrillDownId}
      />
    </FilterPlatesProvider>
  );
};

export default AddMetricButtonContainer;
